import { useCallback, type Dispatch, type MutableRefObject } from "react";
import {
  deleteMessageById,
  getContact,
  getMessage,
  getOutboxEntry,
  mergeMessagePatch,
  removeOutboxEntry,
  saveMessage,
  saveOutboxEntry,
  type MessageRow,
} from "../../../lib/db";
import { flushPendingOutbox } from "./useOutboxFlush";
import {
  apiDeleteVaultMessage,
  apiFetchEncryptedBlob,
  apiFetchVaultBlob,
  apiFetchWorkspaceBlob,
  type ApiUploadProgress,
  apiPostVaultMessage,
  apiUploadEncryptedBlob,
  apiUploadVaultBlob,
  apiUploadWorkspaceTopicBlob,
} from "../../../lib/authApi";
import {
  serverWorkspaceMessageToPlaintext,
  workspaceTopicPeerKey,
} from "../../../lib/workspaceTopicMessages";
import {
  b64ToU8,
  decryptImageBytes,
  deriveVaultKey,
  encryptDirectMessage,
  encryptImageBytes,
  e2eAttachmentBlobIdsFromPlaintext,
  encryptVaultMessage,
  envelopeFile,
  envelopeImage,
  envelopeReaction,
  envelopeText,
  envelopeVoice,
  isChatFileAttachment,
  isChatImageAttachment,
  isChatVoiceAttachment,
  isWorkspaceServerFileAttachment,
  isWorkspaceServerImageAttachment,
  isWorkspaceServerVoiceAttachment,
  isTombstonePlaintext,
  parseMessageEnvelope,
  plaintextDisplayBody,
  type FileAttachmentMetaE2e,
  type IdentityKeys,
  type ImageAttachmentMetaE2e,
  type VoiceAttachmentMetaE2e,
  TOMBSTONE_ENVELOPE_PLAINTEXT,
  workspaceTopicFileBodyJson,
  workspaceTopicImageBodyJson,
  workspaceTopicVoiceBodyJson,
  type WorkspaceServerFileAttachment,
  type WorkspaceServerImageAttachment,
  type WorkspaceServerVoiceAttachment,
  u8ToB64,
  vaultPeerKey,
} from "../../../lib/crypto";
import { newFrameId } from "../../../lib/frame";
import {
  MAX_ENCRYPTED_CIPHER_BYTES,
  MAX_ENCRYPTED_UPLOAD_BYTES,
  prepareImageForSend,
} from "../../../lib/imageAttachment";
import { prepareFileForSend } from "../../../lib/fileAttachment";
import {
  dmPeerIdFromConversation,
  vaultIdFromConversation,
  type ChatAction,
  type ChatState,
  type ConversationRef,
} from "../../state/chatState";

const MAX_WORKSPACE_MESSAGE_UTF8_BYTES = 16000;

function splitByUtf8ByteLimit(text: string, maxBytes: number): string[] {
  if (!text) return [];
  const out: string[] = [];
  let rest = text;
  while (rest.length > 0) {
    if (new TextEncoder().encode(rest).byteLength <= maxBytes) {
      out.push(rest);
      break;
    }
    let lo = 1;
    let hi = rest.length;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      const bytes = new TextEncoder().encode(rest.slice(0, mid)).byteLength;
      if (bytes <= maxBytes) lo = mid;
      else hi = mid - 1;
    }
    const chunk = rest.slice(0, lo);
    if (!chunk) break;
    out.push(chunk);
    rest = rest.slice(lo);
  }
  return out;
}

function revokeDraftPreview(draft: ChatState["chatAttachmentDraft"]): void {
  if (draft?.kind === "image" && draft.previewUrl) {
    URL.revokeObjectURL(draft.previewUrl);
  }
}

/** Server only accepts reply_to that exists in workspace_messages — not optimistic temp ids. */
function workspaceReplyToTargetId(chatState: ChatState, peerKey: string): string | null {
  const d = chatState.chatReplyTo;
  if (!d) return null;
  const parent = chatState.messages.find((m) => m.id === d.messageId);
  if (!parent || parent.peerId !== peerKey) return null;
  if (parent.pending) return null;
  return d.messageId;
}

function forwardTextFromPlaintext(plaintext: string): string {
  const env = parseMessageEnvelope(plaintext);
  if (!env || env.content_type === "reaction" || env.content_type === "deleted") {
    return plaintextDisplayBody(plaintext);
  }
  if (env.content_type === "voice") return "";
  return typeof env.body === "string" ? env.body : plaintextDisplayBody(plaintext);
}

async function downloadForwardAttachmentBytes(
  sourceConversation: ConversationRef,
  attachment:
    | ImageAttachmentMetaE2e
    | FileAttachmentMetaE2e
    | VoiceAttachmentMetaE2e
    | WorkspaceServerImageAttachment
    | WorkspaceServerFileAttachment
    | WorkspaceServerVoiceAttachment,
): Promise<Uint8Array> {
  if (
    isWorkspaceServerImageAttachment(attachment) ||
    isWorkspaceServerFileAttachment(attachment) ||
    isWorkspaceServerVoiceAttachment(attachment)
  ) {
    const buf = await apiFetchWorkspaceBlob(attachment.workspace_id, attachment.blob_id);
    return new Uint8Array(buf);
  }

  const fetchCiphertext =
    sourceConversation.kind === "vault"
      ? apiFetchVaultBlob
      : apiFetchEncryptedBlob;
  const buf = await fetchCiphertext(attachment.blob_id);
  return decryptImageBytes(
    new Uint8Array(buf),
    b64ToU8(attachment.nonce_b64),
    b64ToU8(attachment.key_b64),
  );
}

export function useChatSendPipeline(
  chatState: ChatState,
  dispatchChat: Dispatch<ChatAction>,
  keysRef: MutableRefObject<IdentityKeys | null>,
  selectedConversationRef: MutableRefObject<ConversationRef | null>,
  sendRealtimeCommand: (type: string, payload: Record<string, unknown>, id?: string) => boolean,
  notifyMessagesChanged: (peerId: string) => void,
) {
  const sendPlaintextMessage = useCallback(
    async (peerId: string, plaintext: string): Promise<boolean> => {
      const activeKeys = keysRef.current;
      if (!activeKeys) return false;

      const peer = await getContact(peerId);
      if (!peer) return false;

      const messageId = crypto.randomUUID();
      const { nonce, ciphertext } = encryptDirectMessage(
        plaintext,
        b64ToU8(peer.public_key_x),
        activeKeys.secretKeyX,
      );
      const { nonce: senderNonce, ciphertext: senderCiphertext } = encryptDirectMessage(
        plaintext,
        activeKeys.publicKeyX,
        activeKeys.secretKeyX,
      );

      const nextMessage: MessageRow = {
        id: messageId,
        peerId,
        from_id: activeKeys.userId,
        to_id: peerId,
        created_at: Date.now(),
        plaintext,
        pending: true,
        seenLocal: true,
      };

      await saveMessage(nextMessage);
      notifyMessagesChanged(peerId);
      if (dmPeerIdFromConversation(selectedConversationRef.current) === peerId) {
        dispatchChat({ type: "upsert-message", message: nextMessage });
      }

      const attachment_blob_ids = e2eAttachmentBlobIdsFromPlaintext(plaintext);
      const dmPayload = {
        to: peerId,
        nonce: u8ToB64(nonce),
        ciphertext: u8ToB64(ciphertext),
        sender_nonce: u8ToB64(senderNonce),
        sender_ciphertext: u8ToB64(senderCiphertext),
        ...(attachment_blob_ids.length > 0 ? { attachment_blob_ids } : {}),
      };

      const sent = sendRealtimeCommand("msg.send", dmPayload, messageId);
      if (!sent) {
        try {
          await saveOutboxEntry({
            id: messageId,
            kind: "dm",
            peerId,
            created_at: nextMessage.created_at,
            attempts: 0,
            dmPayload,
          });
          await mergeMessagePatch(messageId, { queued: true });
          notifyMessagesChanged(peerId);
          if (dmPeerIdFromConversation(selectedConversationRef.current) === peerId) {
            dispatchChat({ type: "patch-message", messageId, patch: { queued: true } });
          }
          dispatchChat({
            type: "set-error",
            error: "Реле недоступно — сообщение сохранено локально и отправится после переподключения",
          });
        } catch {
          dispatchChat({
            type: "set-error",
            error: "Очередь отправки переполнена — освободите место или попробуйте позже",
          });
        }
      } else {
        await mergeMessagePatch(messageId, { queued: false });
        if (dmPeerIdFromConversation(selectedConversationRef.current) === peerId) {
          dispatchChat({ type: "patch-message", messageId, patch: { queued: false } });
        }
      }
      sendRealtimeCommand("presence.update", { status: "idle" }, newFrameId());
      return sent;
    },
    [dispatchChat, keysRef, notifyMessagesChanged, selectedConversationRef, sendRealtimeCommand],
  );

  const sendVaultPlaintext = useCallback(
    async (vaultId: string, plaintext: string): Promise<boolean> => {
      const activeKeys = keysRef.current;
      if (!activeKeys) return false;
      const vaultKey = deriveVaultKey(activeKeys.secretKeyX);
      const messageId = crypto.randomUUID();
      const { nonce, ciphertext } = encryptVaultMessage(plaintext, vaultKey);
      const peerKey = vaultPeerKey(vaultId);
      const nextMessage: MessageRow = {
        id: messageId,
        peerId: peerKey,
        from_id: activeKeys.userId,
        to_id: activeKeys.userId,
        created_at: Date.now(),
        plaintext,
        pending: true,
        seenLocal: true,
      };
      await saveMessage(nextMessage);
      notifyMessagesChanged(peerKey);
      if (vaultIdFromConversation(selectedConversationRef.current) === vaultId) {
        dispatchChat({ type: "upsert-message", message: nextMessage });
      }
      try {
        const vaultBlobIds = e2eAttachmentBlobIdsFromPlaintext(plaintext);
        await apiPostVaultMessage(
          vaultId,
          u8ToB64(nonce),
          u8ToB64(ciphertext),
          messageId,
          vaultBlobIds.length > 0 ? vaultBlobIds : undefined,
        );
        await mergeMessagePatch(messageId, { pending: false });
        notifyMessagesChanged(peerKey);
        if (vaultIdFromConversation(selectedConversationRef.current) === vaultId) {
          dispatchChat({ type: "patch-message", messageId, patch: { pending: false } });
        }
        sendRealtimeCommand("presence.update", { status: "idle" }, newFrameId());
        return true;
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        await mergeMessagePatch(messageId, { pending: false, failed: true });
        notifyMessagesChanged(peerKey);
        if (vaultIdFromConversation(selectedConversationRef.current) === vaultId) {
          dispatchChat({ type: "patch-message", messageId, patch: { pending: false, failed: true } });
        }
        dispatchChat({ type: "set-error", error: msg });
        return false;
      }
    },
    [dispatchChat, keysRef, notifyMessagesChanged, selectedConversationRef, sendRealtimeCommand],
  );

  const sendWorkspacePayload = useCallback(
    async (
      workspaceId: string,
      topicId: string,
      body: string,
      plaintext: string,
      replyTo: string | null = null,
    ): Promise<boolean> => {
      const me = keysRef.current?.userId;
      if (!me) return false;
      const peerKey = workspaceTopicPeerKey(workspaceId, topicId);
      const tempId = crypto.randomUUID();
      const created = Date.now();
      const msgRow: MessageRow = {
        id: tempId,
        peerId: peerKey,
        from_id: me,
        to_id: topicId,
        created_at: created,
        plaintext,
        pending: true,
        seenLocal: true,
      };
      await saveMessage(msgRow);
      notifyMessagesChanged(peerKey);
      const sel0 = selectedConversationRef.current;
      if (sel0?.kind === "workspace-topic" && sel0.workspaceId === workspaceId && sel0.topicId === topicId) {
        dispatchChat({ type: "upsert-message", message: msgRow });
      }

      const wsPayload = {
        workspace_id: workspaceId,
        topic_id: topicId,
        body,
        ...(replyTo ? { reply_to: replyTo } : {}),
      };

      try {
        await saveOutboxEntry({
          id: tempId,
          kind: "workspace-topic",
          peerId: peerKey,
          created_at: created,
          attempts: 0,
          wsPayload,
        });
      } catch {
        dispatchChat({
          type: "set-error",
          error: "Очередь отправки переполнена — освободите место или попробуйте позже",
        });
        return false;
      }

      const sent = sendRealtimeCommand("workspace.msg.send", wsPayload, tempId);
      if (!sent) {
        await mergeMessagePatch(tempId, { queued: true });
        notifyMessagesChanged(peerKey);
        const sel1 = selectedConversationRef.current;
        if (sel1?.kind === "workspace-topic" && sel1.workspaceId === workspaceId && sel1.topicId === topicId) {
          dispatchChat({ type: "patch-message", messageId: tempId, patch: { queued: true } });
        }
        dispatchChat({
          type: "set-error",
          error: "Реле недоступно — сообщение в очереди и отправится после переподключения",
        });
        return false;
      }

      const entry = await getOutboxEntry(tempId);
      if (entry) {
        await saveOutboxEntry({ ...entry, sentAtLeastOnce: true, attempts: 0 });
      }
      await mergeMessagePatch(tempId, { queued: false });
      notifyMessagesChanged(peerKey);
      const sel2 = selectedConversationRef.current;
      if (sel2?.kind === "workspace-topic" && sel2.workspaceId === workspaceId && sel2.topicId === topicId) {
        dispatchChat({ type: "patch-message", messageId: tempId, patch: { queued: false } });
      }
      return true;
    },
    [dispatchChat, keysRef, notifyMessagesChanged, selectedConversationRef, sendRealtimeCommand],
  );

  const runUploadWithUi = useCallback(
    async <T>(
      options: {
        prepareLabel?: string;
        uploadLabel: string;
        processingLabel?: string;
        task: (startProgress: (totalHint?: number | null) => (progress: ApiUploadProgress) => void) => Promise<T>;
      },
    ): Promise<T> => {
      const MIN_PREPARE_VISIBILITY_MS = 320;
      const prepareShownAt = options.prepareLabel ? Date.now() : 0;
      let prepareFlushTimer: ReturnType<typeof setTimeout> | null = null;
      let queuedUpload:
        | {
            phase: ApiUploadProgress["phase"];
            label: string;
            loaded: number;
            total: number | null;
          }
        | null = null;

      const pushUploadState = (next: NonNullable<typeof queuedUpload>) => {
        if (!options.prepareLabel) {
          dispatchChat({ type: "set-chat-upload", upload: next });
          return;
        }
        const remaining = MIN_PREPARE_VISIBILITY_MS - (Date.now() - prepareShownAt);
        if (remaining <= 0) {
          queuedUpload = null;
          dispatchChat({ type: "set-chat-upload", upload: next });
          return;
        }
        queuedUpload = next;
        if (prepareFlushTimer !== null) return;
        prepareFlushTimer = setTimeout(() => {
          prepareFlushTimer = null;
          if (!queuedUpload) return;
          dispatchChat({ type: "set-chat-upload", upload: queuedUpload });
          queuedUpload = null;
        }, remaining);
      };

      if (options.prepareLabel) {
        dispatchChat({
          type: "set-chat-upload",
          upload: { phase: "preparing", label: options.prepareLabel, loaded: 0, total: null },
        });
      }
      const startProgress = (totalHint?: number | null) => {
        pushUploadState({
          phase: "uploading",
          label: options.uploadLabel,
          loaded: 0,
          total: totalHint ?? null,
        });
        return (progress: ApiUploadProgress) => {
          pushUploadState({
            phase: progress.phase,
            label:
              progress.phase === "processing"
                ? (options.processingLabel ?? "Сохраняем...")
                : options.uploadLabel,
            loaded: progress.loaded,
            total: progress.total ?? totalHint ?? null,
          });
        };
      };
      try {
        return await options.task(startProgress);
      } finally {
        if (prepareFlushTimer !== null) {
          clearTimeout(prepareFlushTimer);
          prepareFlushTimer = null;
        }
        if (options.prepareLabel) {
          const remaining = MIN_PREPARE_VISIBILITY_MS - (Date.now() - prepareShownAt);
          if (remaining > 0) {
            await new Promise((resolve) => setTimeout(resolve, remaining));
          }
        }
        dispatchChat({ type: "set-chat-upload", upload: null });
      }
    },
    [dispatchChat],
  );

  const sendChat = useCallback(async () => {
    const wsRef = chatState.selectedConversation;
    if (wsRef?.kind === "workspace-topic") {
      const caption = chatState.chatInput.trim();
      const draft = chatState.chatAttachmentDraft;
      if (!draft && !caption) return;
      const { workspaceId, topicId } = wsRef;
      const peerKey = workspaceTopicPeerKey(workspaceId, topicId);
      if (!keysRef.current?.userId) return;

      const replyToId = workspaceReplyToTargetId(chatState, peerKey);

      let bodyStr: string;
      if (draft) {
        if (draft.plainBytes.byteLength > MAX_ENCRYPTED_UPLOAD_BYTES) {
          dispatchChat({
            type: "set-error",
            error: draft.kind === "image" ? "Изображение слишком большое" : "Файл слишком большой",
          });
          return;
        }
        let blobId: string;
        try {
          blobId = await runUploadWithUi({
            uploadLabel: draft.kind === "image" ? "Отправляем изображение..." : "Отправляем файл...",
            processingLabel:
              draft.kind === "image"
                ? "Сохраняем изображение на сервере..."
                : "Сохраняем файл на сервере...",
            task: async (startProgress) =>
              (
                await apiUploadWorkspaceTopicBlob(
                  workspaceId,
                  topicId,
                  draft.plainBytes,
                  startProgress(draft.plainBytes.byteLength),
                )
              ).blob_id,
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          dispatchChat({ type: "set-error", error: message });
          return;
        }
        if (draft.kind === "image") {
          const att: WorkspaceServerImageAttachment = {
            kind: "image",
            storage: "server",
            workspace_id: workspaceId,
            blob_id: blobId,
            mime: draft.mime,
            width: draft.width,
            height: draft.height,
          };
          bodyStr = workspaceTopicImageBodyJson(caption, att);
        } else {
          const att: WorkspaceServerFileAttachment = {
            kind: "file",
            storage: "server",
            workspace_id: workspaceId,
            blob_id: blobId,
            mime: draft.mime,
            filename: draft.filename,
            byte_length: draft.plainBytes.byteLength,
          };
          bodyStr = workspaceTopicFileBodyJson(caption, att);
        }
        revokeDraftPreview(draft);
        dispatchChat({ type: "set-chat-attachment-draft", draft: null });
        dispatchChat({ type: "set-chat-input", value: "" });

        const plain = serverWorkspaceMessageToPlaintext(bodyStr, replyToId);
        const ok = await sendWorkspacePayload(workspaceId, topicId, bodyStr, plain, replyToId);
        if (!ok) return;
        dispatchChat({ type: "set-chat-reply-to", draft: null });
        return;
      }

      const chunks = splitByUtf8ByteLimit(caption, MAX_WORKSPACE_MESSAGE_UTF8_BYTES);
      if (chunks.length === 0) return;
      for (let i = 0; i < chunks.length; i += 1) {
        const chunk = chunks[i]!;
        const replyFor = i === 0 ? replyToId : null;
        const plain = envelopeText(chunk, replyFor);
        const ok = await sendWorkspacePayload(workspaceId, topicId, chunk, plain, replyFor);
        if (!ok) return;
      }
      dispatchChat({ type: "set-chat-input", value: "" });
      dispatchChat({ type: "set-chat-reply-to", draft: null });
      return;
    }

    const vaultId = vaultIdFromConversation(chatState.selectedConversation);
    if (vaultId && keysRef.current) {
      const draft = chatState.chatAttachmentDraft;
      const caption = chatState.chatInput.trim();
      if (!draft && !caption) return;
      const peerKey = vaultPeerKey(vaultId);
      const replyToId: string | null = (() => {
        const d = chatState.chatReplyTo;
        if (!d) return null;
        const parent = chatState.messages.find((m) => m.id === d.messageId);
        if (!parent || parent.peerId !== peerKey) return null;
        if (parent.pending) return null;
        return d.messageId;
      })();
      let plaintext: string;
      if (draft) {
        try {
          const { blobId, key, nonce, encrypted } = await runUploadWithUi({
            prepareLabel: draft.kind === "image" ? "Шифруем изображение..." : "Шифруем файл...",
            uploadLabel: draft.kind === "image" ? "Отправляем изображение..." : "Отправляем файл...",
            processingLabel:
              draft.kind === "image"
                ? "Сохраняем изображение на сервере..."
                : "Сохраняем файл на сервере...",
            task: async (startProgress) => {
              const { encrypted, key, nonce } = encryptImageBytes(draft.plainBytes);
              if (encrypted.byteLength > MAX_ENCRYPTED_CIPHER_BYTES) {
                throw new Error(
                  draft.kind === "image"
                    ? "Изображение слишком большое после шифрования"
                    : "Файл слишком большой после шифрования",
                );
              }
              const blobId = (
                await apiUploadVaultBlob(vaultId, encrypted, startProgress(encrypted.byteLength))
              ).blob_id;
              return { blobId, encrypted, key, nonce };
            },
          });
          if (draft.kind === "image") {
            const att: ImageAttachmentMetaE2e = {
              kind: "image",
              blob_id: blobId,
              mime: draft.mime,
              width: draft.width,
              height: draft.height,
              cipher_byte_length: encrypted.byteLength,
              key_b64: u8ToB64(key),
              nonce_b64: u8ToB64(nonce),
            };
            plaintext = envelopeImage(caption, att, replyToId);
          } else {
            const att: FileAttachmentMetaE2e = {
              kind: "file",
              blob_id: blobId,
              mime: draft.mime,
              filename: draft.filename,
              cipher_byte_length: encrypted.byteLength,
              key_b64: u8ToB64(key),
              nonce_b64: u8ToB64(nonce),
            };
            plaintext = envelopeFile(caption, att, replyToId);
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          dispatchChat({ type: "set-error", error: message });
          return;
        }
        revokeDraftPreview(draft);
        dispatchChat({ type: "set-chat-attachment-draft", draft: null });
        dispatchChat({ type: "set-chat-input", value: "" });
      } else {
        plaintext = envelopeText(caption, replyToId);
      }
      await sendVaultPlaintext(vaultId, plaintext);
      dispatchChat({ type: "set-chat-reply-to", draft: null });
      if (!draft) {
        dispatchChat({ type: "set-chat-input", value: "" });
      }
      return;
    }

    const draft = chatState.chatAttachmentDraft;
    const caption = chatState.chatInput.trim();
    const dmPeerId = dmPeerIdFromConversation(chatState.selectedConversation);
    if (!dmPeerId || !keysRef.current) return;
    if (!draft && !caption) return;

    const replyToId: string | null = (() => {
      const d = chatState.chatReplyTo;
      if (!d || !dmPeerId) return null;
      const parent = chatState.messages.find((m) => m.id === d.messageId);
      if (!parent || parent.peerId !== dmPeerId) return null;
      if (parent.pending) return null;
      return d.messageId;
    })();

    let plaintext: string;
    if (draft) {
      try {
        const { blobId, key, nonce, encrypted } = await runUploadWithUi({
          prepareLabel: draft.kind === "image" ? "Шифруем изображение..." : "Шифруем файл...",
          uploadLabel: draft.kind === "image" ? "Отправляем изображение..." : "Отправляем файл...",
          processingLabel:
            draft.kind === "image"
              ? "Сохраняем изображение на сервере..."
              : "Сохраняем файл на сервере...",
          task: async (startProgress) => {
            const { encrypted, key, nonce } = encryptImageBytes(draft.plainBytes);
            if (encrypted.byteLength > MAX_ENCRYPTED_CIPHER_BYTES) {
              throw new Error(
                draft.kind === "image"
                  ? "Изображение слишком большое после шифрования"
                  : "Файл слишком большой после шифрования",
              );
            }
            const blobId = (
              await apiUploadEncryptedBlob(dmPeerId, encrypted, startProgress(encrypted.byteLength))
            ).blob_id;
            return { blobId, encrypted, key, nonce };
          },
        });
        if (draft.kind === "image") {
          const att: ImageAttachmentMetaE2e = {
            kind: "image",
            blob_id: blobId,
            mime: draft.mime,
            width: draft.width,
            height: draft.height,
            cipher_byte_length: encrypted.byteLength,
            key_b64: u8ToB64(key),
            nonce_b64: u8ToB64(nonce),
          };
          plaintext = envelopeImage(caption, att, replyToId);
        } else {
          const att: FileAttachmentMetaE2e = {
            kind: "file",
            blob_id: blobId,
            mime: draft.mime,
            filename: draft.filename,
            cipher_byte_length: encrypted.byteLength,
            key_b64: u8ToB64(key),
            nonce_b64: u8ToB64(nonce),
          };
          plaintext = envelopeFile(caption, att, replyToId);
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        dispatchChat({ type: "set-error", error: message });
        return;
      }
      revokeDraftPreview(draft);
      dispatchChat({ type: "set-chat-attachment-draft", draft: null });
      dispatchChat({ type: "set-chat-input", value: "" });
    } else {
      plaintext = envelopeText(caption, replyToId);
    }

    await sendPlaintextMessage(dmPeerId, plaintext);
    dispatchChat({ type: "set-chat-reply-to", draft: null });
    if (!draft) {
      dispatchChat({ type: "set-chat-input", value: "" });
    }
  }, [
    chatState,
    dispatchChat,
    keysRef,
    notifyMessagesChanged,
    selectedConversationRef,
    runUploadWithUi,
    sendPlaintextMessage,
    sendVaultPlaintext,
    sendRealtimeCommand,
  ]);

  const toggleReaction = useCallback(
    async (message: MessageRow, emoji: string, remove: boolean) => {
      if (message.deleted || isTombstonePlaintext(message.plaintext)) return;
      const ref = chatState.selectedConversation;
      if (ref?.kind === "workspace-topic") {
        const peerKey = workspaceTopicPeerKey(ref.workspaceId, ref.topicId);
        if (message.peerId !== peerKey) return;
        sendRealtimeCommand(
          "workspace.reaction.toggle",
          {
            workspace_id: ref.workspaceId,
            topic_id: ref.topicId,
            message_id: message.id,
            emoji,
            action: remove ? "remove" : "add",
          },
          newFrameId(),
        );
        return;
      }
      const vid = vaultIdFromConversation(ref);
      if (vid && message.peerId === vaultPeerKey(vid)) {
        const plaintext = envelopeReaction(message.id, emoji, remove ? "remove" : "add");
        await sendVaultPlaintext(vid, plaintext);
        return;
      }
      const dmPeerId = dmPeerIdFromConversation(ref);
      if (!dmPeerId || message.peerId !== dmPeerId) return;
      const plaintext = envelopeReaction(message.id, emoji, remove ? "remove" : "add");
      await sendPlaintextMessage(dmPeerId, plaintext);
    },
    [chatState.selectedConversation, sendPlaintextMessage, sendVaultPlaintext, sendRealtimeCommand],
  );

  const deleteMessage = useCallback(
    async (message: MessageRow) => {
      if (message.deleted || isTombstonePlaintext(message.plaintext)) return;
      const ref = chatState.selectedConversation;
      if (ref?.kind === "workspace-topic") {
        const peerKey = workspaceTopicPeerKey(ref.workspaceId, ref.topicId);
        if (message.peerId !== peerKey) return;
        void removeOutboxEntry(message.id).catch(() => {});
        void deleteMessageById(message.id).catch(() => {});
        if (message.pending) {
          dispatchChat({ type: "remove-message", messageId: message.id });
          notifyMessagesChanged(peerKey);
          return;
        }
        sendRealtimeCommand(
          "workspace.msg.delete",
          {
            workspace_id: ref.workspaceId,
            topic_id: ref.topicId,
            message_id: message.id,
          },
          newFrameId(),
        );
        return;
      }
      const vid = vaultIdFromConversation(ref);
      if (vid && message.peerId === vaultPeerKey(vid)) {
        const activeKeys = keysRef.current;
        if (!activeKeys || message.from_id !== activeKeys.userId) return;
        if (message.pending) {
          await removeOutboxEntry(message.id).catch(() => {});
          await deleteMessageById(message.id).catch(() => {});
          dispatchChat({ type: "remove-message", messageId: message.id });
          notifyMessagesChanged(vaultPeerKey(vid));
          return;
        }
        try {
          await apiDeleteVaultMessage(vid, message.id);
        } catch {
          dispatchChat({ type: "set-error", error: "Не удалось удалить сообщение" });
          return;
        }
        const next: MessageRow = {
          ...message,
          plaintext: TOMBSTONE_ENVELOPE_PLAINTEXT,
          deleted: true,
        };
        await saveMessage(next);
        notifyMessagesChanged(vaultPeerKey(vid));
        if (vaultIdFromConversation(selectedConversationRef.current) === vid) {
          dispatchChat({
            type: "patch-message",
            messageId: message.id,
            patch: { plaintext: TOMBSTONE_ENVELOPE_PLAINTEXT, deleted: true },
          });
        }
        return;
      }
      const dmPeerId = dmPeerIdFromConversation(ref);
      if (!dmPeerId || message.peerId !== dmPeerId) return;
      const activeKeys = keysRef.current;
      if (!activeKeys || message.from_id !== activeKeys.userId) return;

      await removeOutboxEntry(message.id).catch(() => {});

      const next: MessageRow = {
        ...message,
        plaintext: TOMBSTONE_ENVELOPE_PLAINTEXT,
        deleted: true,
      };
      await saveMessage(next);
      notifyMessagesChanged(dmPeerId);
      if (dmPeerIdFromConversation(selectedConversationRef.current) === dmPeerId) {
        dispatchChat({
          type: "patch-message",
          messageId: message.id,
          patch: { plaintext: TOMBSTONE_ENVELOPE_PLAINTEXT, deleted: true },
        });
      }
      sendRealtimeCommand("msg.delete", { message_id: message.id }, newFrameId());
    },
    [
      chatState.selectedConversation,
      dispatchChat,
      keysRef,
      notifyMessagesChanged,
      selectedConversationRef,
      sendRealtimeCommand,
    ],
  );

  const pickChatImage = useCallback(
    async (file: File | null) => {
      if (!file) return;
      const prev = chatState.chatAttachmentDraft;
      try {
        const prepared = await prepareImageForSend(file);
        const previewUrl = URL.createObjectURL(
          new Blob([new Uint8Array(prepared.plainBytes)], { type: prepared.mime }),
        );
        revokeDraftPreview(prev);
        dispatchChat({
          type: "set-chat-attachment-draft",
          draft: { kind: "image", ...prepared, previewUrl },
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        dispatchChat({ type: "set-error", error: message });
      }
    },
    [chatState.chatAttachmentDraft, dispatchChat],
  );

  const pickChatFile = useCallback(
    async (file: File | null) => {
      if (!file) return;
      const prev = chatState.chatAttachmentDraft;
      try {
        const prepared = await prepareFileForSend(file);
        revokeDraftPreview(prev);
        dispatchChat({
          type: "set-chat-attachment-draft",
          draft: { kind: "file", ...prepared },
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        dispatchChat({ type: "set-error", error: message });
      }
    },
    [chatState.chatAttachmentDraft, dispatchChat],
  );

  const removeChatImage = useCallback(() => {
    const d = chatState.chatAttachmentDraft;
    revokeDraftPreview(d);
    dispatchChat({ type: "set-chat-attachment-draft", draft: null });
  }, [chatState.chatAttachmentDraft, dispatchChat]);

  const retryFailedOutboxSend = useCallback(
    async (messageId: string) => {
      const entry = await getOutboxEntry(messageId);
      if (!entry) {
        const row = await getMessage(messageId);
        if (row?.failed && row.peerId.startsWith("vault:")) {
          const vid = row.peerId.slice("vault:".length);
          const keys = keysRef.current;
          if (!keys) return;
          const vaultKey = deriveVaultKey(keys.secretKeyX);
          const { nonce, ciphertext } = encryptVaultMessage(row.plaintext, vaultKey);
          await mergeMessagePatch(messageId, { failed: false, pending: true });
          notifyMessagesChanged(row.peerId);
          const curSel = selectedConversationRef.current;
          if (curSel?.kind === "vault" && curSel.vaultId === vid) {
            dispatchChat({
              type: "patch-message",
              messageId,
              patch: { failed: false, pending: true },
            });
          }
          try {
            const vaultBlobIds = e2eAttachmentBlobIdsFromPlaintext(row.plaintext);
            await apiPostVaultMessage(
              vid,
              u8ToB64(nonce),
              u8ToB64(ciphertext),
              messageId,
              vaultBlobIds.length > 0 ? vaultBlobIds : undefined,
            );
            await mergeMessagePatch(messageId, { pending: false });
            notifyMessagesChanged(row.peerId);
            const cur = selectedConversationRef.current;
            if (cur?.kind === "vault" && cur.vaultId === vid) {
              dispatchChat({ type: "patch-message", messageId, patch: { pending: false } });
            }
          } catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            await mergeMessagePatch(messageId, { pending: false, failed: true });
            notifyMessagesChanged(row.peerId);
            const cur = selectedConversationRef.current;
            if (cur?.kind === "vault" && cur.vaultId === vid) {
              dispatchChat({ type: "patch-message", messageId, patch: { pending: false, failed: true } });
            }
            dispatchChat({ type: "set-error", error: msg });
          }
        }
        return;
      }
      await saveOutboxEntry({ ...entry, attempts: 0 });
      await mergeMessagePatch(messageId, { failed: false, queued: true, pending: true });
      notifyMessagesChanged(entry.peerId);
      const sel = selectedConversationRef.current;
      const peer = entry.peerId;
      if (sel?.kind === "dm" && dmPeerIdFromConversation(sel) === peer) {
        dispatchChat({
          type: "patch-message",
          messageId,
          patch: { failed: false, queued: true, pending: true },
        });
      } else if (sel?.kind === "workspace-topic") {
        const pk = workspaceTopicPeerKey(sel.workspaceId, sel.topicId);
        if (pk === peer) {
          dispatchChat({
            type: "patch-message",
            messageId,
            patch: { failed: false, queued: true, pending: true },
          });
        }
      } else if (sel?.kind === "vault" && peer === vaultPeerKey(sel.vaultId)) {
        dispatchChat({
          type: "patch-message",
          messageId,
          patch: { failed: false, queued: true, pending: true },
        });
      }
      await flushPendingOutbox(
        sendRealtimeCommand,
        notifyMessagesChanged,
        dispatchChat,
        () => selectedConversationRef.current,
      );
    },
    [dispatchChat, notifyMessagesChanged, selectedConversationRef, sendRealtimeCommand],
  );

  const sendVoiceMessage = useCallback(
    async (plainBytes: Uint8Array, mime: string, durationMs: number, waveform: number[]) => {
      const wsRef = chatState.selectedConversation;

      if (wsRef?.kind === "workspace-topic") {
        const { workspaceId, topicId } = wsRef;
        const peerKey = workspaceTopicPeerKey(workspaceId, topicId);
        const me = keysRef.current?.userId;
        if (!me) return;

        let blobId: string;
        try {
          blobId = await runUploadWithUi({
            uploadLabel: "Отправляем голосовое...",
            processingLabel: "Сохраняем голосовое на сервере...",
            task: async (startProgress) =>
              (
                await apiUploadWorkspaceTopicBlob(
                  workspaceId,
                  topicId,
                  plainBytes,
                  startProgress(plainBytes.byteLength),
                )
              ).blob_id,
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          dispatchChat({ type: "set-error", error: message });
          return;
        }

        const att: WorkspaceServerVoiceAttachment = {
          kind: "voice",
          storage: "server",
          workspace_id: workspaceId,
          blob_id: blobId,
          mime,
          duration_ms: durationMs,
          waveform,
        };
        const bodyStr = workspaceTopicVoiceBodyJson(att);
        const replyToId = workspaceReplyToTargetId(chatState, peerKey);
        const plaintext = serverWorkspaceMessageToPlaintext(bodyStr, replyToId);

        const tempId = crypto.randomUUID();
        const created = Date.now();
        const msgRow: MessageRow = {
          id: tempId,
          peerId: peerKey,
          from_id: me,
          to_id: topicId,
          created_at: created,
          plaintext,
          pending: true,
          seenLocal: true,
        };
        await saveMessage(msgRow);
        notifyMessagesChanged(peerKey);
        const sel = selectedConversationRef.current;
        if (sel?.kind === "workspace-topic" && sel.workspaceId === workspaceId && sel.topicId === topicId) {
          dispatchChat({ type: "upsert-message", message: msgRow });
        }

        const wsPayload = {
          workspace_id: workspaceId,
          topic_id: topicId,
          body: bodyStr,
          ...(replyToId ? { reply_to: replyToId } : {}),
        };
        try {
          await saveOutboxEntry({ id: tempId, kind: "workspace-topic", peerId: peerKey, created_at: created, attempts: 0, wsPayload });
        } catch {
          dispatchChat({ type: "set-error", error: "Очередь отправки переполнена" });
          return;
        }

        const sent = sendRealtimeCommand("workspace.msg.send", wsPayload, tempId);
        if (!sent) {
          await mergeMessagePatch(tempId, { queued: true });
          notifyMessagesChanged(peerKey);
          const sel1 = selectedConversationRef.current;
          if (sel1?.kind === "workspace-topic" && sel1.workspaceId === workspaceId && sel1.topicId === topicId) {
            dispatchChat({ type: "patch-message", messageId: tempId, patch: { queued: true } });
          }
          dispatchChat({ type: "set-error", error: "Реле недоступно — сообщение в очереди" });
          return;
        }
        const entry = await getOutboxEntry(tempId);
        if (entry) await saveOutboxEntry({ ...entry, sentAtLeastOnce: true, attempts: 0 });
        await mergeMessagePatch(tempId, { queued: false });
        notifyMessagesChanged(peerKey);
        dispatchChat({ type: "set-chat-reply-to", draft: null });
        return;
      }

      const vId = vaultIdFromConversation(wsRef);
      if (vId && keysRef.current) {
        try {
          const { blobId, key, nonce, encrypted } = await runUploadWithUi({
            prepareLabel: "Шифруем голосовое...",
            uploadLabel: "Отправляем голосовое...",
            processingLabel: "Сохраняем голосовое на сервере...",
            task: async (startProgress) => {
              const { encrypted, key, nonce } = encryptImageBytes(plainBytes);
              if (encrypted.byteLength > MAX_ENCRYPTED_CIPHER_BYTES) {
                throw new Error("Голосовое сообщение слишком большое");
              }
              const blobId = (
                await apiUploadVaultBlob(vId, encrypted, startProgress(encrypted.byteLength))
              ).blob_id;
              return { blobId, encrypted, key, nonce };
            },
          });
          const att: VoiceAttachmentMetaE2e = {
            kind: "voice",
            blob_id: blobId,
            mime,
            duration_ms: durationMs,
            waveform,
            cipher_byte_length: encrypted.byteLength,
            key_b64: u8ToB64(key),
            nonce_b64: u8ToB64(nonce),
          };
          const plaintext = envelopeVoice(att);
          await sendVaultPlaintext(vId, plaintext);
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          dispatchChat({ type: "set-error", error: message });
          return;
        }
        return;
      }

      // DM
      const dmPeerId = dmPeerIdFromConversation(wsRef);
      if (!dmPeerId || !keysRef.current) return;

      try {
        const { blobId, key, nonce, encrypted } = await runUploadWithUi({
          prepareLabel: "Шифруем голосовое...",
          uploadLabel: "Отправляем голосовое...",
          processingLabel: "Сохраняем голосовое на сервере...",
          task: async (startProgress) => {
            const { encrypted, key, nonce } = encryptImageBytes(plainBytes);
            if (encrypted.byteLength > MAX_ENCRYPTED_CIPHER_BYTES) {
              throw new Error("Голосовое сообщение слишком большое");
            }
            const blobId = (
              await apiUploadEncryptedBlob(dmPeerId, encrypted, startProgress(encrypted.byteLength))
            ).blob_id;
            return { blobId, encrypted, key, nonce };
          },
        });
        const att: VoiceAttachmentMetaE2e = {
          kind: "voice",
          blob_id: blobId,
          mime,
          duration_ms: durationMs,
          waveform,
          cipher_byte_length: encrypted.byteLength,
          key_b64: u8ToB64(key),
          nonce_b64: u8ToB64(nonce),
        };
        const plaintext = envelopeVoice(att);
        await sendPlaintextMessage(dmPeerId, plaintext);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        dispatchChat({ type: "set-error", error: message });
        return;
      }
    },
    [
      chatState,
      keysRef,
      dispatchChat,
      notifyMessagesChanged,
      selectedConversationRef,
      runUploadWithUi,
      sendRealtimeCommand,
      sendPlaintextMessage,
      sendVaultPlaintext,
    ],
  );

  const forwardMessageToConversation = useCallback(
    async (
      message: MessageRow,
      sourceConversation: ConversationRef,
      targetConversation: ConversationRef,
    ): Promise<boolean> => {
      if (message.deleted || isTombstonePlaintext(message.plaintext)) return false;
      const env = parseMessageEnvelope(message.plaintext);
      if (env?.content_type === "reaction" || env?.content_type === "deleted") return false;

      const sendForwardPlaintext = async (plaintext: string): Promise<boolean> => {
        if (targetConversation.kind === "dm") {
          return sendPlaintextMessage(targetConversation.peerId, plaintext);
        }
        if (targetConversation.kind === "vault") {
          return sendVaultPlaintext(targetConversation.vaultId, plaintext);
        }
        const chunks = splitByUtf8ByteLimit(forwardTextFromPlaintext(plaintext), MAX_WORKSPACE_MESSAGE_UTF8_BYTES);
        for (const chunk of chunks) {
          const ok = await sendWorkspacePayload(
            targetConversation.workspaceId,
            targetConversation.topicId,
            chunk,
            envelopeText(chunk, null),
            null,
          );
          if (!ok) return false;
        }
        return true;
      };

      try {
        if (!env || env.content_type === "text") {
          const forwardedText = forwardTextFromPlaintext(message.plaintext);
          if (!forwardedText) return false;
          return await sendForwardPlaintext(envelopeText(forwardedText, null));
        }

        if (env.content_type === "image") {
          const imageAtt = env.attachments[0];
          if (imageAtt === undefined || !isChatImageAttachment(imageAtt)) return false;
          const plainBytes = await downloadForwardAttachmentBytes(sourceConversation, imageAtt);
          const caption = env.body ?? "";

          if (targetConversation.kind === "workspace-topic") {
            if (plainBytes.byteLength > MAX_ENCRYPTED_UPLOAD_BYTES) {
              throw new Error("Изображение слишком большое");
            }
            const blobId = await runUploadWithUi({
              uploadLabel: "Отправляем изображение...",
              processingLabel: "Сохраняем изображение на сервере...",
              task: async (startProgress) =>
                (
                  await apiUploadWorkspaceTopicBlob(
                    targetConversation.workspaceId,
                    targetConversation.topicId,
                    plainBytes,
                    startProgress(plainBytes.byteLength),
                  )
                ).blob_id,
            });
            const attachment: WorkspaceServerImageAttachment = {
              kind: "image",
              storage: "server",
              workspace_id: targetConversation.workspaceId,
              blob_id: blobId,
              mime: imageAtt.mime,
              width: imageAtt.width,
              height: imageAtt.height,
            };
            const body = workspaceTopicImageBodyJson(caption, attachment);
            const plaintext = serverWorkspaceMessageToPlaintext(body, null);
            return await sendWorkspacePayload(
              targetConversation.workspaceId,
              targetConversation.topicId,
              body,
              plaintext,
              null,
            );
          }

          const { blobId, encrypted, key, nonce } = await runUploadWithUi({
            prepareLabel: "Шифруем изображение...",
            uploadLabel: "Отправляем изображение...",
            processingLabel: "Сохраняем изображение на сервере...",
            task: async (startProgress) => {
              const { encrypted, key, nonce } = encryptImageBytes(plainBytes);
              if (encrypted.byteLength > MAX_ENCRYPTED_CIPHER_BYTES) {
                throw new Error("Изображение слишком большое после шифрования");
              }
              const blobId =
                targetConversation.kind === "vault"
                  ? (
                      await apiUploadVaultBlob(
                        targetConversation.vaultId,
                        encrypted,
                        startProgress(encrypted.byteLength),
                      )
                    ).blob_id
                  : (
                      await apiUploadEncryptedBlob(
                        targetConversation.peerId,
                        encrypted,
                        startProgress(encrypted.byteLength),
                      )
                    ).blob_id;
              return { blobId, encrypted, key, nonce };
            },
          });
          const attachment: ImageAttachmentMetaE2e = {
            kind: "image",
            blob_id: blobId,
            mime: imageAtt.mime,
            width: imageAtt.width,
            height: imageAtt.height,
            cipher_byte_length: encrypted.byteLength,
            key_b64: u8ToB64(key),
            nonce_b64: u8ToB64(nonce),
          };
          return await sendForwardPlaintext(envelopeImage(caption, attachment, null));
        }

        if (env.content_type === "file") {
          const fileAtt = env.attachments[0];
          if (fileAtt === undefined || !isChatFileAttachment(fileAtt)) return false;
          const plainBytes = await downloadForwardAttachmentBytes(sourceConversation, fileAtt);
          const caption = env.body ?? "";

          if (targetConversation.kind === "workspace-topic") {
            if (plainBytes.byteLength > MAX_ENCRYPTED_UPLOAD_BYTES) {
              throw new Error("Файл слишком большой");
            }
            const blobId = await runUploadWithUi({
              uploadLabel: "Отправляем файл...",
              processingLabel: "Сохраняем файл на сервере...",
              task: async (startProgress) =>
                (
                  await apiUploadWorkspaceTopicBlob(
                    targetConversation.workspaceId,
                    targetConversation.topicId,
                    plainBytes,
                    startProgress(plainBytes.byteLength),
                  )
                ).blob_id,
            });
            const attachment: WorkspaceServerFileAttachment = {
              kind: "file",
              storage: "server",
              workspace_id: targetConversation.workspaceId,
              blob_id: blobId,
              mime: fileAtt.mime,
              filename: fileAtt.filename,
              byte_length: plainBytes.byteLength,
            };
            const body = workspaceTopicFileBodyJson(caption, attachment);
            const plaintext = serverWorkspaceMessageToPlaintext(body, null);
            return await sendWorkspacePayload(
              targetConversation.workspaceId,
              targetConversation.topicId,
              body,
              plaintext,
              null,
            );
          }

          const { blobId, encrypted, key, nonce } = await runUploadWithUi({
            prepareLabel: "Шифруем файл...",
            uploadLabel: "Отправляем файл...",
            processingLabel: "Сохраняем файл на сервере...",
            task: async (startProgress) => {
              const { encrypted, key, nonce } = encryptImageBytes(plainBytes);
              if (encrypted.byteLength > MAX_ENCRYPTED_CIPHER_BYTES) {
                throw new Error("Файл слишком большой после шифрования");
              }
              const blobId =
                targetConversation.kind === "vault"
                  ? (
                      await apiUploadVaultBlob(
                        targetConversation.vaultId,
                        encrypted,
                        startProgress(encrypted.byteLength),
                      )
                    ).blob_id
                  : (
                      await apiUploadEncryptedBlob(
                        targetConversation.peerId,
                        encrypted,
                        startProgress(encrypted.byteLength),
                      )
                    ).blob_id;
              return { blobId, encrypted, key, nonce };
            },
          });
          const attachment: FileAttachmentMetaE2e = {
            kind: "file",
            blob_id: blobId,
            mime: fileAtt.mime,
            filename: fileAtt.filename,
            cipher_byte_length: encrypted.byteLength,
            key_b64: u8ToB64(key),
            nonce_b64: u8ToB64(nonce),
          };
          return await sendForwardPlaintext(envelopeFile(caption, attachment, null));
        }

        if (env.content_type === "voice") {
          const voiceAtt = env.attachments[0];
          if (voiceAtt === undefined || !isChatVoiceAttachment(voiceAtt)) return false;
          const plainBytes = await downloadForwardAttachmentBytes(sourceConversation, voiceAtt);

          if (targetConversation.kind === "workspace-topic") {
            if (plainBytes.byteLength > MAX_ENCRYPTED_UPLOAD_BYTES) {
              throw new Error("Голосовое сообщение слишком большое");
            }
            const blobId = await runUploadWithUi({
              uploadLabel: "Отправляем голосовое...",
              processingLabel: "Сохраняем голосовое на сервере...",
              task: async (startProgress) =>
                (
                  await apiUploadWorkspaceTopicBlob(
                    targetConversation.workspaceId,
                    targetConversation.topicId,
                    plainBytes,
                    startProgress(plainBytes.byteLength),
                  )
                ).blob_id,
            });
            const attachment: WorkspaceServerVoiceAttachment = {
              kind: "voice",
              storage: "server",
              workspace_id: targetConversation.workspaceId,
              blob_id: blobId,
              mime: voiceAtt.mime,
              duration_ms: voiceAtt.duration_ms,
              waveform: voiceAtt.waveform,
            };
            const body = workspaceTopicVoiceBodyJson(attachment);
            const plaintext = serverWorkspaceMessageToPlaintext(body, null);
            return await sendWorkspacePayload(
              targetConversation.workspaceId,
              targetConversation.topicId,
              body,
              plaintext,
              null,
            );
          }

          const { blobId, encrypted, key, nonce } = await runUploadWithUi({
            prepareLabel: "Шифруем голосовое...",
            uploadLabel: "Отправляем голосовое...",
            processingLabel: "Сохраняем голосовое на сервере...",
            task: async (startProgress) => {
              const { encrypted, key, nonce } = encryptImageBytes(plainBytes);
              if (encrypted.byteLength > MAX_ENCRYPTED_CIPHER_BYTES) {
                throw new Error("Голосовое сообщение слишком большое");
              }
              const blobId =
                targetConversation.kind === "vault"
                  ? (
                      await apiUploadVaultBlob(
                        targetConversation.vaultId,
                        encrypted,
                        startProgress(encrypted.byteLength),
                      )
                    ).blob_id
                  : (
                      await apiUploadEncryptedBlob(
                        targetConversation.peerId,
                        encrypted,
                        startProgress(encrypted.byteLength),
                      )
                    ).blob_id;
              return { blobId, encrypted, key, nonce };
            },
          });
          const attachment: VoiceAttachmentMetaE2e = {
            kind: "voice",
            blob_id: blobId,
            mime: voiceAtt.mime,
            duration_ms: voiceAtt.duration_ms,
            waveform: voiceAtt.waveform,
            cipher_byte_length: encrypted.byteLength,
            key_b64: u8ToB64(key),
            nonce_b64: u8ToB64(nonce),
          };
          return await sendForwardPlaintext(envelopeVoice(attachment, null));
        }
      } catch (error) {
        dispatchChat({
          type: "set-error",
          error: error instanceof Error ? error.message : String(error),
        });
        return false;
      }
      return false;
    },
    [dispatchChat, runUploadWithUi, sendPlaintextMessage, sendVaultPlaintext, sendWorkspacePayload],
  );

  return {
    forwardMessageToConversation,
    sendPlaintextMessage,
    sendChat,
    toggleReaction,
    deleteMessage,
    pickChatImage,
    pickChatFile,
    removeChatImage,
    retryFailedOutboxSend,
    sendVoiceMessage,
  };
}
